import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { can, deleteFix, listFixes, type Fix } from "../api";
import FixSummary from "../components/FixSummary";
import MutationError from "../components/MutationError";
import SheetHeader from "../components/SheetHeader";
import { useT, type Messages } from "../i18n";
import { primaryOrg, useSession } from "../session";

/**
 * FixList is the corrections that reach this chapter, behind one small button.
 *
 * It was a panel on the page — a row per correction between the narration
 * controls and the text — so a chapter with a dozen corrections pushed the
 * chapter itself off the first screen. The list is reference, opened now and
 * then, so it lives in a sheet like the chapter's other tools.
 *
 * The one thing that must not hide is a detached fix. An occurrence fix names
 * one span of one revision; after an edit that span identifies nothing, so the
 * fix is excluded from the render rather than applied to whatever has moved
 * into its place (ADR-0009). That exclusion was already correct and completely
 * silent — the author would hear an uncorrected word and have no way to find
 * out why. So the button itself turns amber while any are being skipped.
 */
export default function FixList({ chapterID }: { chapterID: string }) {
  const { me } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const t = useT();
  const [open, setOpen] = useState(false);

  const fixes = useQuery({
    queryKey: ["fixes", chapterID],
    queryFn: () => listFixes(chapterID),
    enabled: can(org, "fix.write"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFix(id),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["fixes", chapterID] });
      // Removing a fix restores a render key the chapter has held before, so
      // the staleness answer changes too.
      await client.invalidateQueries({ queryKey: ["estimate", chapterID] });
    },
  });

  const items = fixes.data?.items ?? [];
  if (items.length === 0) return null;

  const detached = items.filter((f) => f.detached);

  return (
    <>
      <button
        type="button"
        data-testid="open-fixes"
        data-detached={detached.length > 0 ? detached.length : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 rounded-control border px-2.5 py-1 text-xs transition-colors hover:bg-sunk ${
          detached.length > 0
            ? "border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-400"
            : "border-line text-ink-soft"
        }`}
      >
        {detached.length > 0 && (
          <span aria-hidden="true" className="size-1.5 rounded-full bg-amber-500" />
        )}
        {t.fixes.open(items.length)}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t.fixes.heading}
          data-testid="fix-list"
          /* Above the transport and the header, like the other sheets. */
          className="fixed inset-x-0 bottom-0 z-40 flex max-h-[85dvh] flex-col rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
        >
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-col">
            <SheetHeader
              title={`${t.fixes.heading} (${t.meta.num(items.length)})`}
              onClose={() => setOpen(false)}
              closeTestID="close-fixes"
            />
            <div className="overflow-y-auto px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {detached.length > 0 && (
                <p
                  role="alert"
                  data-testid="detached-warning"
                  className="mb-3 text-sm text-amber-700 dark:text-amber-400"
                >
                  {t.fixes.detached(detached.length)}
                </p>
              )}

              <ul className="space-y-2">
                {items.map((fix) => (
                  <li
                    key={fix.id}
                    data-testid={fix.detached ? "fix-detached" : "fix"}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    {/* dir="auto" on this block and nowhere inside it: the from/to
                        words are Persian and must not carry their own dir, or this
                        element finds no strong character and lays out left to right. */}
                    <span
                      dir="auto"
                      className={fix.detached || fix.superseded ? "opacity-60" : undefined}
                    >
                      <FixSummary fix={fix} />
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted">{scopeLabel(fix, t)}</span>
                      <button
                        type="button"
                        data-testid="delete-fix"
                        aria-label={t.fixes.deleteFor(fix.from_text)}
                        onClick={() => remove.mutate(fix.id)}
                        className="rounded-control border border-line px-2 py-1 text-xs"
                      >
                        {t.common.delete}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>

              {/* A correction that was not deleted is a correction the next
                  render still applies, which is the opposite of what the
                  author just asked for. */}
              <MutationError
                className="mt-2"
                testId="delete-fix-error"
                error={remove.error}
                fallback={t.fixes.deleteFailed}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function scopeLabel(fix: Fix, t: Messages): string {
  if (fix.detached) return t.fixes.scopes.skipped;
  // A correction a narrower one has taken over changes nothing here, and
  // saying only where it lives would leave the author reading two corrections
  // for one word with no way to tell which is heard (ADR-0036).
  if (fix.superseded) return t.fixes.scopes.overridden;
  return t.fixes.scopes[fix.scope];
}
